import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { vitalsApi } from '../../lib/api/vitals';
import { LoadingState } from '../common/LoadingState';

interface NurseVitalsManagementProps {
  /** Single assigned patient (nurses always have only ONE patient) */
  patient?: {
    id: number;
    name: string;
    umr_no: string;
    age?: number;
    gender?: string;
  } | null;
}

export function NurseVitalsManagement({ patient }: NurseVitalsManagementProps) {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingVital, setEditingVital] = useState<any | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Fetch vitals for assigned patient (last 30 days)
  const [vitalsData, setVitalsData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchVitals = async () => {
    if (!patient?.id) return;
    
    try {
      setIsLoading(true);
      const data = await vitalsApi.getPatientVitals(patient.id, { days: 30 });
      setVitalsData(data);
    } catch (error) {
      console.error('Failed to fetch vitals:', error);
      toast.error('Failed to load vitals');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch vitals when patient changes
  useEffect(() => {
    if (patient?.id) {
      fetchVitals();
    }
  }, [patient?.id]);

  const refetch = fetchVitals;

  const [formData, setFormData] = useState({
    spo2: '',
    heart_rate: '',
    systolic_bp: '',
    diastolic_bp: '',
    temperature: '',
    respiratory_rate: '',
    weight: '',
    notes: ''
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      spo2: '',
      heart_rate: '',
      systolic_bp: '',
      diastolic_bp: '',
      temperature: '',
      respiratory_rate: '',
      weight: '',
      notes: ''
    });
  };

  const handleAdd = async () => {
    if (!patient?.id) {
      toast.error('No patient assigned');
      return;
    }

    try {
      await vitalsApi.recordVitals({
        patient_id: patient.id,
        spo2: parseInt(formData.spo2),
        heart_rate: parseInt(formData.heart_rate),
        systolic_bp: parseInt(formData.systolic_bp),
        diastolic_bp: parseInt(formData.diastolic_bp),
        temperature: formData.temperature ? parseFloat(formData.temperature) : undefined,
        respiratory_rate: formData.respiratory_rate ? parseInt(formData.respiratory_rate) : undefined,
        weight: formData.weight ? parseFloat(formData.weight) : undefined,
        notes: formData.notes || undefined
      });

      toast.success('Vitals recorded successfully!');
      setIsAddDialogOpen(false);
      resetForm();
      refetch();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to record vitals');
    }
  };

  const handleEdit = async () => {
    if (!editingVital) return;

    try {
      await vitalsApi.updateVitals(editingVital.id, {
        spo2: formData.spo2 ? parseInt(formData.spo2) : undefined,
        heart_rate: formData.heart_rate ? parseInt(formData.heart_rate) : undefined,
        systolic_bp: formData.systolic_bp ? parseInt(formData.systolic_bp) : undefined,
        diastolic_bp: formData.diastolic_bp ? parseInt(formData.diastolic_bp) : undefined,
        temperature: formData.temperature ? parseFloat(formData.temperature) : undefined,
        respiratory_rate: formData.respiratory_rate ? parseInt(formData.respiratory_rate) : undefined,
        weight: formData.weight ? parseFloat(formData.weight) : undefined,
        notes: formData.notes || undefined
      });

      toast.success('Vitals updated successfully!');
      setEditingVital(null);
      resetForm();
      refetch();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update vitals');
    }
  };

  const handleDelete = async () => {
    if (!deletingId) return;

    try {
      await vitalsApi.deleteVitals(deletingId);
      toast.success('Vitals record deleted');
      setDeletingId(null);
      refetch();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to delete vitals');
    }
  };

  const openEditDialog = (vital: any) => {
    setEditingVital(vital);
    setFormData({
      spo2: vital.spo2?.toString() || '',
      heart_rate: vital.heart_rate?.toString() || '',
      systolic_bp: vital.systolic_bp?.toString() || '',  // ✅ Fixed: was vital.systolic
      diastolic_bp: vital.diastolic_bp?.toString() || '',  // ✅ Fixed: was vital.diastolic
      temperature: vital.temperature?.toString() || '',
      respiratory_rate: vital.respiratory_rate?.toString() || '',
      weight: vital.weight?.toString() || '',
      notes: vital.notes || ''
    });
  };

  const getVitalStatus = (vital: any) => {
    // Check for abnormal values
    if (vital.spo2 < 92) return { color: 'destructive', text: 'Low SpO₂' };
    if (vital.heart_rate > 100 || vital.heart_rate < 50) return { color: 'warning', text: 'Abnormal HR' };
    if (vital.systolic_bp > 140 || vital.diastolic_bp > 90) return { color: 'warning', text: 'High BP' };  // ✅ Fixed: was vital.systolic/diastolic
    return { color: 'success', text: 'Normal' };
  };

  const vitals = vitalsData?.vitals || [];

  // No patient assigned
  if (!patient) {
    return (
      <Card className="shadow-lg">
        <CardContent className="p-6">
          <EmptyState
            title="No patient assigned"
            description="You don't have a patient assigned yet. Contact your coordinator for a patient assignment."
            icon={User}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Patient Info Card */}
      <Card className="shadow-lg border-primary/20">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-muted-foreground">Assigned Patient</p>
              <p className="text-lg text-primary">{patient.name}</p>
              <p className="text-sm text-muted-foreground">
                UMR: {patient.umr_no}
                {patient.age && patient.gender && ` • ${patient.age}yrs, ${patient.gender}`}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Header with Record Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-primary">Vitals History</h2>
          <p className="text-sm text-muted-foreground">Last 30 days of vital signs</p>
        </div>
        
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90">
              <Plus className="w-4 h-4 mr-2" />
              Record Vitals
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Record Patient Vitals</DialogTitle>
              <DialogDescription>
                Recording vitals for {patient.name}
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid grid-cols-2 gap-4">
              {/* Required Fields */}
              <div>
                <Label htmlFor="spo2">SpO₂ (%) *</Label>
                <Input
                  id="spo2"
                  type="number"
                  min="0"
                  max="100"
                  value={formData.spo2}
                  onChange={(e) => handleInputChange('spo2', e.target.value)}
                  placeholder="95"
                  required
                />
              </div>

              <div>
                <Label htmlFor="heart_rate">Heart Rate (bpm) *</Label>
                <Input
                  id="heart_rate"
                  type="number"
                  min="0"
                  max="300"
                  value={formData.heart_rate}
                  onChange={(e) => handleInputChange('heart_rate', e.target.value)}
                  placeholder="72"
                  required
                />
              </div>

              <div>
                <Label htmlFor="systolic_bp">Systolic BP (mmHg) *</Label>
                <Input
                  id="systolic_bp"
                  type="number"
                  min="0"
                  max="300"
                  value={formData.systolic_bp}
                  onChange={(e) => handleInputChange('systolic_bp', e.target.value)}
                  placeholder="120"
                  required
                />
              </div>

              <div>
                <Label htmlFor="diastolic_bp">Diastolic BP (mmHg) *</Label>
                <Input
                  id="diastolic_bp"
                  type="number"
                  min="0"
                  max="200"
                  value={formData.diastolic_bp}
                  onChange={(e) => handleInputChange('diastolic_bp', e.target.value)}
                  placeholder="80"
                  required
                />
              </div>

              {/* Optional Fields */}
              <div>
                <Label htmlFor="temperature">Temperature (°F)</Label>
                <Input
                  id="temperature"
                  type="number"
                  step="0.1"
                  min="90"
                  max="110"
                  value={formData.temperature}
                  onChange={(e) => handleInputChange('temperature', e.target.value)}
                  placeholder="98.6"
                />
              </div>

              <div>
                <Label htmlFor="respiratory_rate">Respiratory Rate (breaths/min)</Label>
                <Input
                  id="respiratory_rate"
                  type="number"
                  min="0"
                  max="60"
                  value={formData.respiratory_rate}
                  onChange={(e) => handleInputChange('respiratory_rate', e.target.value)}
                  placeholder="16"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="weight">Weight (kg)</Label>
                <Input
                  id="weight"
                  type="number"
                  step="0.1"
                  min="0"
                  max="500"
                  value={formData.weight}
                  onChange={(e) => handleInputChange('weight', e.target.value)}
                  placeholder="70"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="notes">Notes</Label>
                <Input
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Observations from home visit..."
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsAddDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAdd}
                disabled={!formData.spo2 || !formData.heart_rate || !formData.systolic_bp || !formData.diastolic_bp}
              >
                Record Vitals
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Vitals List */}
      {isLoading ? (
        <LoadingState message="Loading vitals..." />
      ) : vitals.length === 0 ? (
        <EmptyState
          title="No vitals recorded"
          description="Start recording patient vitals during home visits"
          icon={Activity}
        />
      ) : (
        <div className="space-y-3">
          {vitals.map((vital: any) => {
            const status = getVitalStatus(vital);
            
            return (
              <Card key={vital.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <p className="text-sm">{formatDate(vital.date)}</p>
                        <span className="text-xs text-muted-foreground">{formatTime(vital.time)}</span>
                        <Badge variant={status.color as any} className="text-xs">
                          {status.text}
                        </Badge>
                        {vital.recorded_by && (
                          <span className="text-xs text-muted-foreground">
                            By: {vital.recorded_by}
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="flex items-center gap-2">
                          <Droplet className="w-4 h-4 text-[#14CAF4]" />
                          <div>
                            <p className="text-xs text-muted-foreground">SpO₂</p>
                            <p className="text-sm">{vital.spo2}%</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <Heart className="w-4 h-4 text-destructive" />
                          <div>
                            <p className="text-xs text-muted-foreground">Heart Rate</p>
                            <p className="text-sm">{vital.heart_rate} bpm</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-[#F07D13]" />
                          <div>
                            <p className="text-xs text-muted-foreground">Blood Pressure</p>
                            <p className="text-sm">{vital.systolic_bp}/{vital.diastolic_bp}</p>
                          </div>
                        </div>

                        {vital.temperature && (
                          <div className="flex items-center gap-2">
                            <Thermometer className="w-4 h-4 text-primary" />
                            <div>
                              <p className="text-xs text-muted-foreground">Temperature</p>
                              <p className="text-sm">{vital.temperature}°F</p>
                            </div>
                          </div>
                        )}
                      </div>

                      {vital.notes && (
                        <p className="text-xs text-muted-foreground mt-2 italic">
                          Note: {vital.notes}
                        </p>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEditDialog(vital)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setDeletingId(vital.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editingVital} onOpenChange={(open) => !open && setEditingVital(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Vitals</DialogTitle>
            <DialogDescription>Update patient vital signs measurements</DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit-spo2">SpO₂ (%)</Label>
              <Input
                id="edit-spo2"
                type="number"
                value={formData.spo2}
                onChange={(e) => handleInputChange('spo2', e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="edit-heart_rate">Heart Rate (bpm)</Label>
              <Input
                id="edit-heart_rate"
                type="number"
                value={formData.heart_rate}
                onChange={(e) => handleInputChange('heart_rate', e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="edit-systolic_bp">Systolic BP</Label>
              <Input
                id="edit-systolic_bp"
                type="number"
                value={formData.systolic_bp}
                onChange={(e) => handleInputChange('systolic_bp', e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="edit-diastolic_bp">Diastolic BP</Label>
              <Input
                id="edit-diastolic_bp"
                type="number"
                value={formData.diastolic_bp}
                onChange={(e) => handleInputChange('diastolic_bp', e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="edit-temperature">Temperature (°F)</Label>
              <Input
                id="edit-temperature"
                type="number"
                step="0.1"
                value={formData.temperature}
                onChange={(e) => handleInputChange('temperature', e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="edit-respiratory_rate">Respiratory Rate</Label>
              <Input
                id="edit-respiratory_rate"
                type="number"
                value={formData.respiratory_rate}
                onChange={(e) => handleInputChange('respiratory_rate', e.target.value)}
              />
            </div>

            <div className="col-span-2">
              <Label htmlFor="edit-weight">Weight (kg)</Label>
              <Input
                id="edit-weight"
                type="number"
                step="0.1"
                value={formData.weight}
                onChange={(e) => handleInputChange('weight', e.target.value)}
              />
            </div>

            <div className="col-span-2">
              <Label htmlFor="edit-notes">Notes</Label>
              <Input
                id="edit-notes"
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditingVital(null);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleEdit}>
              Update Vitals
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingId} onOpenChange={(open) => !open && setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Vitals Record?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this vitals record. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}